import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import api from '../api';
import { useAuth } from '../context/auth';
import { usePlayback } from '../context/playback';
import { LoadingText } from '../components/Style';
import {
  MusicContainer,
  MusicCard,
  TabBar,
  Tab,
  CommunitySection,
  EmptyText,
  HeroPlayButton,
  SectionHeading,
  BackChevron,
} from '../components/MusicPageStyles';
import { ReactComponent as PlayIcon } from '../img/play.svg';
import { ReactComponent as PauseIcon } from '../img/pause.svg';
import ShareModal from '../components/ShareModal';
import SignupPromptModal from '../components/SignupPromptModal';
import CompactPostCard from '../components/CompactPostCard';
import { trackEvent } from '../analytics';
import AddToListSheet from '../components/AddToListSheet';
import PlayQueueMenu from '../components/PlayQueueMenu';

// --- Black banner (matches MediaHeader pattern) ---

var HeaderBanner = styled.div`
  background: black;
  color: white;
  padding: 0.6em 1em;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
`;

var HeaderText = styled.div`
  flex: 1;
  min-width: 0;
`;

var PostAboutButton = styled(Link)`
  background: black;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.6rem 1.25rem;
  font-size: 0.85rem;
  font-weight: 700;
  font-family: inherit;
  text-decoration: none;
  text-align: center;
  width: 100%;
  max-width: 400px;
  box-sizing: border-box;
  display: block;
`;

var PostAboutButtonEl = styled.button`
  background: black;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.6rem 1.25rem;
  font-size: 0.85rem;
  font-weight: 700;
  font-family: inherit;
  text-align: center;
  width: 100%;
  max-width: 400px;
  box-sizing: border-box;
  display: block;
  cursor: pointer;
`;

var HeroArtist = styled.div`
  font-weight: 900;
`;

var HeroTitle = styled.div`
  font-weight: 300;
  font-style: italic;
`;

var HeroAlbum = styled.span`
  font-weight: 300;
  opacity: 0.6;
`;

var HeroArtistLink = styled(Link)`
  color: white;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

var HeroAlbumLink = styled(Link)`
  color: white;
  text-decoration: none;
  font-weight: 300;
  opacity: 0.6;

  &:hover {
    text-decoration: underline;
  }
`;

// --- Artwork (beveled border, matches ArtworkBlock from DisplayPost) ---

var ArtworkBlock = styled.div`
  overflow: hidden;
`;

var HeroArtwork = styled.img`
  width: 100%;
  aspect-ratio: 1;
  object-fit: cover;
  display: block;
`;

var TrackPostLink = styled(Link)`
  width: 26px;
  height: 26px;
  border: 1px solid black;
  border-right-width: 2px;
  border-bottom-width: 2px;
  border-radius: 6px;
  background: none;
  color: black;
  display: flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  font-size: 1.1rem;
  font-weight: 300;
  line-height: 1;
  flex-shrink: 0;
`;

var TrackPostButtonEl = styled.button`
  width: 26px;
  height: 26px;
  border: 1px solid black;
  border-right-width: 2px;
  border-bottom-width: 2px;
  border-radius: 6px;
  background: none;
  color: black;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  font-weight: 300;
  line-height: 1;
  flex-shrink: 0;
  cursor: pointer;
  padding: 0;
`;

// --- Track list styles ---

var TracksSection = styled.div`
  width: 100%;
`;

var TrackRow = styled.div`
  display: flex;
  align-items: center;
  padding: 0.5rem 0;
  border-bottom: 1px solid #eee;
  gap: 0.5rem;
`;

var TrackNumber = styled.span`
  font-size: 0.85rem;
  opacity: 0.5;
  width: 1rem;
  text-align: center;
  flex-shrink: 0;
  margin-left: -0.25rem;
`;

var TrackName = styled.span`
  font-size: 0.9rem;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

var TrackPlayButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 1px solid #ccc;
  background: none;
  color: black;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;

  &:hover {
    background: #f0f0f0;
  }
`;

export default function SongPage() {
  var { mediaId } = useParams();
  var [searchParams] = useSearchParams();
  var navigate = useNavigate();
  var location = useLocation();
  var playback = usePlayback();
  var cachedPreviewRef = useRef(null);

  var _error = useState(null);
  var error = _error[0];
  var setError = _error[1];

  var _loading = useState(true);
  var loading = _loading[0];
  var setLoading = _loading[1];

  var _media = useState(null);
  var media = _media[0];
  var setMedia = _media[1];

  var _posts = useState([]);
  var posts = _posts[0];
  var setPosts = _posts[1];

  var _tracks = useState(null);
  var tracks = _tracks[0];
  var setTracks = _tracks[1];

  var _versions = useState(null);
  var versions = _versions[0];
  var setVersions = _versions[1];

  var _mentionedInPosts = useState([]);
  var mentionedInPosts = _mentionedInPosts[0];
  var setMentionedInPosts = _mentionedInPosts[1];

  var _fetchingPreview = useState(false);
  var fetchingPreview = _fetchingPreview[0];
  var setFetchingPreview = _fetchingPreview[1];

  var _showShare = useState(false);
  var showShare = _showShare[0];
  var setShowShare = _showShare[1];

  var _activeTab = useState(location.state?.activeTab || null);
  var activeTab = _activeTab[0];
  var setActiveTab = _activeTab[1];

  useEffect(
    function () {
      if (media) {
        var mediaTitle = media.song_title || media.album_title;
        document.title = media.artist + ' - ' + mediaTitle + ' | HeavyRotation';
      }
    },
    [media]
  );

  // Determine lookup mode
  var albumArtist = searchParams.get('artist');
  var albumName = searchParams.get('album');
  var songName = searchParams.get('song');
  var urlUpc = searchParams.get('upc');
  var urlCatalogID = searchParams.get('catalogID');
  var urlIsrc = searchParams.get('isrc');
  var isAlbumLookup = !mediaId && albumArtist && albumName && !songName;
  var isSongLookup = !mediaId && albumArtist && songName;

  // Fetch media detail
  useEffect(
    function () {
      setLoading(true);
      setError(null);
      setMedia(null);
      setPosts([]);
      setTracks(null);
      setMentionedInPosts([]);
      cachedPreviewRef.current = null;

      var url;
      if (isSongLookup) {
        url =
          '/api/media/by-song?artist=' +
          encodeURIComponent(albumArtist) +
          '&song=' +
          encodeURIComponent(songName) +
          (urlIsrc ? '&isrc=' + encodeURIComponent(urlIsrc) : '') +
          '&limit=100';
      } else if (isAlbumLookup) {
        url =
          '/api/media/by-album?artist=' +
          encodeURIComponent(albumArtist) +
          '&album=' +
          encodeURIComponent(albumName) +
          (urlCatalogID ? '&catalogID=' + encodeURIComponent(urlCatalogID) : '') +
          (urlUpc ? '&upc=' + encodeURIComponent(urlUpc) : '') +
          '&limit=100';
      } else if (mediaId) {
        url = '/api/media/' + encodeURIComponent(mediaId) + '?limit=100';
      } else {
        setError(true);
        setLoading(false);
        return;
      }

      api
        .get(url)
        .then(function (response) {
          var data = response.data;
          setMedia(data.media);
          var loadedPosts = data.posts?.posts || [];
          setPosts(loadedPosts);
          setMentionedInPosts(data.mentionedInPosts || []);
          setLoading(false);
          if (data.media) {
            trackEvent('music_page_viewed', {
              type: data.media.song_title ? 'song' : 'album',
              title: data.media.song_title || data.media.album_title,
              artist: data.media.artist,
            });
          }
          if (
            activeTab === null ||
            (activeTab !== 'posts' &&
              activeTab !== 'tracks' &&
              activeTab !== 'mentions' &&
              activeTab !== 'versions')
          ) {
            var mentionCount = (data.mentionedInPosts || []).length;
            if (loadedPosts.length > 0) {
              setActiveTab('posts');
            } else if (mentionCount > 0) {
              setActiveTab('mentions');
            } else {
              setActiveTab('tracks');
            }
          }
        })
        .catch(function () {
          setError(true);
          setLoading(false);
        });
    },
    [mediaId, albumArtist, albumName, songName, isAlbumLookup, isSongLookup]
  );

  // Fetch album tracks: by UPC, by artist+album lookup, or by song's parent album
  useEffect(
    function () {
      if (!media) return;
      var trackParams = null;
      if (urlCatalogID) {
        trackParams = { catalogID: urlCatalogID };
      } else if (media.type === 'album' && media.upc) {
        trackParams = { upc: media.upc };
      } else if (isAlbumLookup) {
        trackParams = { artist: albumArtist, album: albumName };
      } else if (media.album_title && media.artist) {
        // Song page: fetch sibling tracks from the parent album
        trackParams = { artist: media.artist, album: media.album_title };
      }
      if (!trackParams) return;
      api
        .get('/api/music/album-tracks', { params: trackParams })
        .then(function (response) {
          setTracks(response.data.tracks || []);
        })
        .catch(function () {});
    },
    [media, isAlbumLookup, albumArtist, albumName]
  );

  // Fetch other versions of this album
  useEffect(
    function () {
      if (!urlCatalogID || !isAlbumLookup) return;
      api
        .get('/api/music/album-versions', { params: { catalogID: urlCatalogID } })
        .then(function (response) {
          var v = response.data.versions || [];
          if (v.length > 0) setVersions(v);
        })
        .catch(function () {});
    },
    [urlCatalogID, isAlbumLookup]
  );

  var authContext = useAuth();
  var authTokens = authContext.authTokens;
  var isLoggedIn = authTokens?.token;

  var addToListState = useState(false);
  var showAddToList = addToListState[0];
  var setShowAddToList = addToListState[1];

  var _showSignupPrompt = useState(false);
  var showSignupPrompt = _showSignupPrompt[0];
  var setShowSignupPrompt = _showSignupPrompt[1];

  var _playMenuPos = useState(null);
  var playMenuPos = _playMenuPos[0];
  var setPlayMenuPos = _playMenuPos[1];

  var pendingPlayFnRef = useRef(null);

  var isSomethingPlaying =
    playback.playbackState === 'playing' ||
    playback.playbackState === 'paused' ||
    playback.playbackState === 'loading';

  function buildQueueItems(trackData) {
    if (!media) return [];
    var artworkURL = media.artwork_url;
    var artist = media.artist;

    if (trackData.tracks) {
      return trackData.tracks.map(function (t) {
        return {
          postId: null,
          title: t.title,
          artist: artist,
          artworkURL: artworkURL,
          catalogID: t.catalogID || null,
          previewURL: t.previewURL || null,
        };
      });
    }

    var songTitle = media.type === 'album' ? media.album_title : media.song_title;
    return [
      {
        postId: null,
        title: songTitle,
        artist: artist,
        artworkURL: artworkURL,
        catalogID: trackData.catalogID || null,
        previewURL: trackData.previewURL || null,
      },
    ];
  }

  function doHeroPlay(mode) {
    if (!media) return;

    setFetchingPreview(true);

    function play(items) {
      if (items.length === 0) return;
      if (mode === 'addToQueue') {
        playback.addToQueue(items);
      } else {
        playback.playNow(items);
      }
    }

    var isAlbumPlay = (media.type === 'album' && media.upc) || isAlbumLookup;
    if (isAlbumPlay) {
      if (tracks) {
        setFetchingPreview(false);
        play(buildQueueItems({ tracks: tracks }));
      } else {
        var trackParams = media.upc
          ? { upc: media.upc }
          : { artist: albumArtist, album: albumName };
        api
          .get('/api/music/album-tracks', { params: trackParams })
          .then(function (response) {
            setFetchingPreview(false);
            var albumTracks = response.data.tracks || [];
            setTracks(albumTracks);
            play(buildQueueItems({ tracks: albumTracks }));
          })
          .catch(function () {
            setFetchingPreview(false);
          });
      }
    } else {
      if (cachedPreviewRef.current) {
        setFetchingPreview(false);
        play(buildQueueItems(cachedPreviewRef.current));
        return;
      }

      var params = media.isrc ? { isrc: media.isrc } : { upc: media.upc };
      api
        .get('/api/music/preview', { params: params })
        .then(function (response) {
          var previewURL = response.data.previewURL || null;
          var catalogID = response.data.catalogID || null;
          cachedPreviewRef.current = { previewURL: previewURL, catalogID: catalogID };
          setFetchingPreview(false);
          play(buildQueueItems({ previewURL: previewURL, catalogID: catalogID }));
        })
        .catch(function () {
          setFetchingPreview(false);
        });
    }
  }

  function handleHeroPlay(e) {
    if (!media) return;
    if (isHeroPlaying) {
      playback.togglePlayPause();
      return;
    }
    if (!isSomethingPlaying) {
      doHeroPlay('playNow');
    } else {
      var rect = e.currentTarget.getBoundingClientRect();
      pendingPlayFnRef.current = doHeroPlay;
      setPlayMenuPos({ x: rect.left, y: rect.top });
    }
  }

  function doTrackPlay(index, mode) {
    if (!tracks || !media) return;
    var artworkURL = media.artwork_url;
    var artist = media.artist;
    var remaining = tracks.slice(index).map(function (t) {
      return {
        postId: null,
        title: t.title,
        artist: artist,
        artworkURL: artworkURL,
        catalogID: t.catalogID || null,
        previewURL: t.previewURL || null,
      };
    });
    if (remaining.length === 0) return;
    if (mode === 'addToQueue') {
      playback.addToQueue(remaining);
    } else {
      playback.playNow(remaining);
    }
  }

  function handleTrackPlay(index, e) {
    if (!tracks || !media) return;

    var track = tracks[index];
    if (
      playback.currentTrack &&
      playback.currentTrack.artist === media.artist &&
      playback.currentTrack.title === track.title
    ) {
      playback.togglePlayPause();
      return;
    }

    if (!isSomethingPlaying) {
      doTrackPlay(index, 'playNow');
    } else {
      var rect = e.currentTarget.getBoundingClientRect();
      pendingPlayFnRef.current = function (mode) {
        doTrackPlay(index, mode);
      };
      setPlayMenuPos({ x: rect.left, y: rect.top });
    }
  }

  if (loading) return <LoadingText>Loading...</LoadingText>;
  if (error) return <EmptyText>Not found.</EmptyText>;

  var isSong = media && media.song_title;
  var title = isSong ? media.song_title : media ? media.album_title : '';
  var year = media && media.release_date ? media.release_date.slice(0, 4) : null;
  var canPlay = media && (!!media.isrc || !!media.upc);
  var genres =
    media && media.genre
      ? media.genre
          .split(',')
          .map(function (g) {
            return g.trim();
          })
          .filter(Boolean)
      : [];
  var shareURL =
    window.location.origin +
    (isAlbumLookup
      ? '/music/album?artist=' +
        encodeURIComponent(albumArtist) +
        '&album=' +
        encodeURIComponent(albumName) +
        (urlCatalogID ? '&catalogID=' + encodeURIComponent(urlCatalogID) : '') +
        (urlUpc ? '&upc=' + encodeURIComponent(urlUpc) : '')
      : isSongLookup
        ? '/music/song?artist=' +
          encodeURIComponent(albumArtist) +
          '&song=' +
          encodeURIComponent(songName) +
          (urlIsrc ? '&isrc=' + encodeURIComponent(urlIsrc) : '')
        : '/music/' + encodeURIComponent(mediaId));
  var shareText = 'Check out ' + (media ? media.artist + ' - ' + title : '') + ' on HeavyRotation';

  // Detect if this media's track is currently playing
  var isHeroPlaying = false;
  var heroPlayState = 'idle';
  if (playback.currentTrack && media) {
    var ct = playback.currentTrack;
    var heroTitle = isSong ? media.song_title : media.album_title;
    if (ct.artist === media.artist && ct.title === heroTitle) {
      isHeroPlaying = true;
      heroPlayState = playback.playbackState;
    }
  }

  var isLoadingPlay = heroPlayState === 'loading' || fetchingPreview;
  var heroPlayIcon = <PlayIcon width="20" height="20" />;
  if (isLoadingPlay) heroPlayIcon = '...';
  if (heroPlayState === 'playing') heroPlayIcon = <PauseIcon width="20" height="20" />;

  var navFrom = location.state || {};
  var backLabel =
    navFrom.from === 'search'
      ? 'Back to Search'
      : navFrom.from === 'feed'
        ? 'Back to Feed'
        : navFrom.from === 'artist'
          ? 'Back to Artist'
          : navFrom.from === 'album'
            ? 'Back to Album'
            : isSong || isSongLookup
              ? 'Back to Album'
              : 'Back to Artist';
  var backPath =
    navFrom.fromPath ||
    (isSong || isSongLookup
      ? '/music/album?artist=' +
        encodeURIComponent(media ? media.artist : albumArtist || '') +
        '&album=' +
        encodeURIComponent(media ? media.album_title || '' : '') +
        (media && media.upc ? '&upc=' + encodeURIComponent(media.upc) : '')
      : '/artist/' +
        encodeURIComponent(media ? media.artist : albumArtist || '') +
        (media && media.apple_artist_id
          ? '?appleId=' + encodeURIComponent(media.apple_artist_id)
          : ''));

  var currentPath = location.pathname + location.search;
  var forwardState = {
    from: isSong || isSongLookup ? 'song' : 'album',
    fromPath: currentPath,
    activeTab: activeTab,
  };

  return (
    <MusicContainer>
      <BackChevron to={backPath} state={navFrom}>
        <svg
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        >
          <polyline points="8 1 3 6 8 11" />
        </svg>
        {backLabel}
      </BackChevron>
      {media && (
        <MusicCard>
          <HeaderBanner>
            <HeaderText>
              <HeroArtist>
                <HeroArtistLink
                  to={
                    '/artist/' +
                    encodeURIComponent(media.artist) +
                    (media.apple_artist_id
                      ? '?appleId=' + encodeURIComponent(media.apple_artist_id)
                      : '')
                  }
                  state={forwardState}
                >
                  {media.artist}
                </HeroArtistLink>
              </HeroArtist>
              <HeroTitle>{title}</HeroTitle>
              {isSong && media.album_title && (
                <HeroAlbumLink
                  to={
                    '/music/album?artist=' +
                    encodeURIComponent(media.artist) +
                    '&album=' +
                    encodeURIComponent(media.album_title) +
                    (media.upc ? '&upc=' + encodeURIComponent(media.upc) : '')
                  }
                >
                  {media.album_title}
                  {year && ' (' + year + ')'}
                </HeroAlbumLink>
              )}
              {!isSong && year && <HeroAlbum>{year}</HeroAlbum>}
            </HeaderText>
            {canPlay && (
              <HeroPlayButton
                style={{ border: '1.5px solid white', color: 'white' }}
                onClick={handleHeroPlay}
                disabled={isLoadingPlay}
              >
                {heroPlayIcon}
              </HeroPlayButton>
            )}
          </HeaderBanner>
          <ArtworkBlock>
            <HeroArtwork src={media.artwork_url} alt={title} />
          </ArtworkBlock>
        </MusicCard>
      )}

      {media && isLoggedIn && (
        <PostAboutButton
          to="/post"
          state={{ media: media }}
          onClick={function () {
            trackEvent('music_post_initiated', {
              type: isSong ? 'song' : 'album',
              title: title,
              artist: media.artist,
              source: 'hero_cta',
            });
          }}
        >
          Post about this {isSong ? 'song' : 'album'}
        </PostAboutButton>
      )}

      {media && isLoggedIn && (
        <PostAboutButtonEl
          onClick={function () {
            trackEvent('music_add_to_list_initiated', {
              type: isSong ? 'song' : 'album',
              title: title,
              artist: media.artist,
              source: 'hero_cta',
            });
            setShowAddToList(true);
          }}
          style={{
            background: 'white',
            color: 'black',
            border: '1px solid black',
            borderRightWidth: '2px',
            borderBottomWidth: '2px',
            cursor: 'pointer',
          }}
        >
          Add to list
        </PostAboutButtonEl>
      )}

      {showAddToList && media && (
        <AddToListSheet
          media={media}
          onClose={function () {
            setShowAddToList(false);
          }}
        />
      )}

      {media && !isLoggedIn && (
        <PostAboutButtonEl
          onClick={function () {
            trackEvent('signup_prompt_shown', { source: 'music_post_button' });
            setShowSignupPrompt(true);
          }}
        >
          Post about this {isSong ? 'song' : 'album'}
        </PostAboutButtonEl>
      )}

      {showSignupPrompt && (
        <SignupPromptModal
          title={
            'Sign up to share this ' +
            (isSong ? 'song' : 'album') +
            ' and discover new music you love.'
          }
          source="music_post_button"
          referer={location.pathname + location.search}
          onClose={function () {
            setShowSignupPrompt(false);
          }}
        />
      )}

      {showShare && (
        <ShareModal
          url={shareURL}
          text={shareText}
          shareType={isSong ? 'song' : 'album'}
          onClose={function () {
            setShowShare(false);
          }}
        />
      )}

      <TabBar>
        <Tab
          $active={activeTab === 'posts'}
          onClick={function () {
            setActiveTab('posts');
          }}
        >
          Posts
        </Tab>
        {mentionedInPosts.length > 0 && (
          <Tab
            $active={activeTab === 'mentions'}
            onClick={function () {
              setActiveTab('mentions');
            }}
          >
            Mentioned in
          </Tab>
        )}
        <Tab
          $active={activeTab === 'tracks'}
          onClick={function () {
            setActiveTab('tracks');
          }}
        >
          {isSong ? 'Other tracks from ' + (media.album_title || 'this album') : 'Tracks'}
        </Tab>
        {versions && versions.length > 0 && (
          <Tab
            $active={activeTab === 'versions'}
            onClick={function () {
              setActiveTab('versions');
            }}
          >
            Other Versions
          </Tab>
        )}
      </TabBar>

      <CommunitySection style={{ display: activeTab === 'posts' ? 'flex' : 'none' }}>
        {(function () {
          if (posts.length > 0) {
            return posts
              .slice()
              .sort(function (a, b) {
                var aHasText = a.text && a.text.trim() ? 1 : 0;
                var bHasText = b.text && b.text.trim() ? 1 : 0;
                return bHasText - aHasText;
              })
              .map(function (post) {
                return <CompactPostCard key={post.id} post={post} />;
              });
          }

          if (!loading) {
            return (
              <EmptyText style={{ marginTop: '0.5rem' }}>
                No posts about this {isSong ? 'song' : 'album'} yet.
              </EmptyText>
            );
          }

          return null;
        })()}
      </CommunitySection>

      <CommunitySection style={{ display: activeTab === 'mentions' ? 'flex' : 'none' }}>
        {mentionedInPosts.length > 0 ? (
          mentionedInPosts.map(function (post) {
            return <CompactPostCard key={post.id} post={post} />;
          })
        ) : (
          <EmptyText style={{ marginTop: '0.5rem' }}>No mentions yet.</EmptyText>
        )}
      </CommunitySection>

      <TracksSection
        style={{
          display: activeTab === 'tracks' && tracks && tracks.length > 0 ? 'block' : 'none',
        }}
      >
        {(tracks || []).map(function (track, i) {
          var isTrackPlaying = false;
          if (playback.currentTrack && media) {
            isTrackPlaying =
              playback.currentTrack.artist === media.artist &&
              playback.currentTrack.title === track.title &&
              playback.playbackState === 'playing';
          }
          return (
            <TrackRow key={track.catalogID || i}>
              <TrackPlayButton
                onClick={function (e) {
                  handleTrackPlay(i, e);
                }}
              >
                {isTrackPlaying ? (
                  <PauseIcon width="14" height="14" />
                ) : (
                  <PlayIcon width="14" height="14" />
                )}
              </TrackPlayButton>
              <TrackNumber>{i + 1}</TrackNumber>
              <TrackName
                onClick={function () {
                  navigate(
                    '/music/song?artist=' +
                      encodeURIComponent(media.artist) +
                      '&song=' +
                      encodeURIComponent(track.title) +
                      (track.isrc ? '&isrc=' + encodeURIComponent(track.isrc) : ''),
                    { state: forwardState }
                  );
                }}
              >
                {track.title}
              </TrackName>
              {isLoggedIn ? (
                <TrackPostLink
                  to="/post"
                  state={{
                    media: {
                      artist: media.artist,
                      song_title: track.title,
                      album_title: media.album_title || media.song_title,
                      artwork_url: track.artwork_url || media.artwork_url,
                      type: 'song',
                      isrc: track.isrc || null,
                      upc: null,
                      release_date: track.releaseDate || null,
                      catalogID: track.catalogID || null,
                      previewURL: track.previewURL || null,
                    },
                  }}
                  title={'Post about ' + track.title}
                  onClick={function () {
                    trackEvent('music_post_initiated', {
                      type: 'song',
                      title: track.title,
                      artist: media.artist,
                      source: 'track_cta',
                    });
                  }}
                >
                  +
                </TrackPostLink>
              ) : (
                <TrackPostButtonEl
                  title={'Post about ' + track.title}
                  onClick={function () {
                    setShowSignupPrompt(true);
                  }}
                >
                  +
                </TrackPostButtonEl>
              )}
            </TrackRow>
          );
        })}
      </TracksSection>

      {activeTab === 'versions' && versions && versions.length > 0 && (
        <div
          style={{
            width: '100%',
            maxWidth: 400,
            display: 'flex',
            flexDirection: 'column',
            gap: '0.75rem',
          }}
        >
          {versions.map(function (v) {
            var year = v.releaseDate ? v.releaseDate.slice(0, 4) : '';
            return (
              <Link
                key={v.catalogID}
                to={
                  '/music/album?artist=' +
                  encodeURIComponent(media.artist) +
                  '&album=' +
                  encodeURIComponent(v.name) +
                  '&catalogID=' +
                  encodeURIComponent(v.catalogID) +
                  (v.upc ? '&upc=' + encodeURIComponent(v.upc) : '')
                }
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  textDecoration: 'none',
                  color: 'inherit',
                  background: 'white',
                  border: '1px solid #ddd',
                  borderRadius: 10,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
                  overflow: 'hidden',
                }}
              >
                {v.artworkURL && (
                  <img
                    src={v.artworkURL}
                    alt={v.name}
                    style={{ width: 64, height: 64, objectFit: 'cover', flexShrink: 0 }}
                  />
                )}
                <div style={{ padding: '0.5rem 0.5rem 0.5rem 0', minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: '0.85rem', lineHeight: 1.3 }}>
                    {v.name}
                  </div>
                  {year && <div style={{ fontSize: '0.75rem', color: '#888' }}>{year}</div>}
                </div>
              </Link>
            );
          })}
        </div>
      )}
      {playMenuPos && (
        <PlayQueueMenu
          pos={playMenuPos}
          onClose={function () {
            setPlayMenuPos(null);
          }}
          onPlayNow={function () {
            if (pendingPlayFnRef.current) {
              pendingPlayFnRef.current('playNow');
              pendingPlayFnRef.current = null;
            }
          }}
          onAddToQueue={function () {
            if (pendingPlayFnRef.current) {
              pendingPlayFnRef.current('addToQueue');
              pendingPlayFnRef.current = null;
            }
          }}
        />
      )}
    </MusicContainer>
  );
}
